import React from 'react';

import { Box } from '@opendoor/bricks/core';
import { Timeline } from '@opendoor/cinderblocks/platform/Timeline';

import { FinalizedOffer, useOfferContext } from 'sellDirect/OfferContext';
import { formatPhoneNumber } from 'shared/helpers/formatters';
import { FinalOfferCard } from 'sellDirect/components/closingDash/finalOffer/FinalOfferCard';
import { WhatWentIntoYourOffer } from 'sellDirect/components/WhatWentIntoYourOffer';
import { analyticsPrefixTimelineCta, TimelineCta } from 'sellDirect/components/TimelineCta';
import {
  SellDirectAgentPreSignTimelineSteps,
  SellDirectPreSignTimelineItems,
  SellDirectPreSignTimelineSteps,
} from 'shared/helpers/constants';
import { FinalUpdatePrompt } from 'sellDirect/components/closingDash/FinalUpdatePrompt';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';
import { RefreshOfferCta } from 'sellDirect/components/offer/RefreshOfferCta';
import { getHubHowItWorksSellRoute } from 'shared/helpers/routes';
import {
  ClosingFAQs,
  getPreSignAgentFaqs,
  getPreSignFaqs,
} from 'sellDirect/components/closingDash/ClosingFAQs';
import { getClosingContractRoute } from 'sellDirect/routes/SellDirectClosingRouter';
import { JourneyCard } from 'shared/components/JourneyCard';
import { SellDirectLayout } from 'sellDirect/components/offer/SellDirectLayout';
import { InternalLink } from 'platform/react-router';
import { useTriggerImpressionOnExperiment } from 'shared/graphql/experimentation/useTriggerOptimizelyImpression';
import {
  DisappointmentMessagingPrompt,
  OfferStage,
} from 'sellDirect/components/offer/DisappointmentMessagingPrompt';
import { EXPERIMENTS } from 'shared/declarations/OptimizelyExperiments';
import { useExperimentationContext } from 'sellDirect/ExperimentationContext';

export interface FinalizedOfferPageProps {
  offer: FinalizedOffer;
}

/**
 *  Shown when the offer is finalized and still valid.
 *
 *  @throws Error if offer in illegal state
 */
export const FinalizedOfferPage: React.FC<FinalizedOfferPageProps> = ({ offer }) => {
  if (offer.isPurchaseAgreementSigned) {
    throw new Error('cannot show FinalizedOfferPage when purchase agreement is signed');
  }

  const {
    loading: offerLoading,
    experimentationData: {
      isInReduceDisappointmentMessagingPostUwExperimentTreatment,
      isInReduceDisappointmentMessagingPostUwExperimentControl,
    },
  } = useOfferContext();
  const {
    assertions: { isnonOwnerOccupiedTreatment },
  } = useExperimentationContext();

  const { isMobile } = useDevice();

  const timelineStepInfo = {
    steps:
      offer.shouldSeeAgentExperience || isnonOwnerOccupiedTreatment
        ? SellDirectAgentPreSignTimelineSteps
        : SellDirectPreSignTimelineSteps,
    currentStepName: SellDirectPreSignTimelineItems.SIGN,
  };

  const preSignFaqs = offer.shouldSeeAgentExperience
    ? getPreSignAgentFaqs({
        name: offer.acqTitleOrganization?.name || '',
        displayPhone: formatPhoneNumber(offer.acqTitleOrganization?.displayPhone),
      })
    : getPreSignFaqs({
        name: offer.acqTitleOrganization?.name || '',
        displayPhone: formatPhoneNumber(offer.acqTitleOrganization?.displayPhone),
      });

  // CTA that will be shown stickyBottomContent on mobile and as normal content otherwise
  let cta = undefined;
  if (offer.isExpired) {
    cta = <RefreshOfferCta mt={7} />;
  } else if (offer.isAccepted) {
    const signYourOfferText = `Next - Sign ${
      offer.shouldSeeAgentExperience ? 'the' : 'your'
    } offer`;
    const signYourOfferButtonAnalyticsId = 'sell-direct-sign';
    const signYourOfferHref = getClosingContractRoute();
    if (isMobile) {
      cta = (
        <Box mx={7} my={5}>
          <InternalLink
            aria-label={null}
            analyticsName={`cake-${analyticsPrefixTimelineCta}${signYourOfferButtonAnalyticsId}`}
            variant="primary-button"
            to={signYourOfferHref}
            width="100%"
            py={4}
          >
            {signYourOfferText}
          </InternalLink>
        </Box>
      );
    } else {
      cta = (
        <TimelineCta
          mt="7"
          title={signYourOfferText}
          buttonText="Get Started"
          analyticsId={signYourOfferButtonAnalyticsId}
          href={signYourOfferHref}
          {...timelineStepInfo}
        />
      );
    }
  }

  /*
   * Trigger impression for the reduce-disapointment-messaging-post-uw experiment
   * if the customer is in control/experiment. Some customers (ie ATX market customers)
   * will not be included in the experiment.
   */
  const inReduceDisappointmentExperiment =
    isInReduceDisappointmentMessagingPostUwExperimentTreatment ||
    isInReduceDisappointmentMessagingPostUwExperimentControl;
  useTriggerImpressionOnExperiment(
    EXPERIMENTS.REDUCE_DISAPPOINTMENT_MESSAGING_POST_UW,
    !inReduceDisappointmentExperiment,
  );

  return (
    <SellDirectLayout
      isLoading={offerLoading}
      address={offer.address}
      stickyBottomContent={isMobile && cta}
    >
      <Box my={7} width="100%">
        {isInReduceDisappointmentMessagingPostUwExperimentTreatment ? (
          <DisappointmentMessagingPrompt offerStage={OfferStage.POST_UW} />
        ) : (
          <FinalUpdatePrompt expired={offer.isExpired} />
        )}
      </Box>
      <FinalOfferCard offer={offer} />
      {!isMobile && cta}
      <WhatWentIntoYourOffer mt="11" />
      <ClosingFAQs faqs={preSignFaqs} mt="10" />

      <Box width="100%" mt={8}>
        <JourneyCard
          message="How selling works"
          showCTA={false}
          url={getHubHowItWorksSellRoute()}
        />
      </Box>
      {isMobile && (
        <Box my={10} data-testid="timeline-cta-mobile">
          <Timeline ariaLabel="Selling timeline" {...timelineStepInfo} />
        </Box>
      )}
    </SellDirectLayout>
  );
};
